import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Banknote, Check, ChevronLeft, ChevronRight, CircleDot, Loader2, Target, X } from "lucide-react";
import {
  formatCasinoCoin,
  formatCasinoOutcomeLabel,
  canAffordCasinoMinBet,
  getMaxCasinoBetForOutcome,
  getSuggestedCasinoBetAmounts,
  getCasinoActionErrorMessage,
  MIN_CASINO_BET,
} from "../../utils/casinoHelpers";
import { OUTCOME_COLORS } from "../../utils/rouletteOutcomes";
import "../../bond-spin-bet.css";

const PlaceBondSpinBetModal = ({
  open,
  onClose,
  game,
  userCoinBalance = 0,
  submitting = false,
  onSubmit,
}) => {
  const [step, setStep] = useState(1);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [exactNumber, setExactNumber] = useState("");
  const [betAmount, setBetAmount] = useState("");
  const [error, setError] = useState("");
  const bodyRef = React.useRef(null);

  const outcomes = useMemo(() => game?.outcomes || [], [game?.outcomes]);
  const isExactFlow = selectedOutcome?.outcomeType === "ExactNumber";
  const numericAmount = Math.floor(Number(betAmount) || 0);
  const numericExact = parseInt(exactNumber, 10);

  const potentialWin = useMemo(() => {
    if (!selectedOutcome || !numericAmount) return 0;
    return Math.floor(numericAmount * Number(selectedOutcome.multiplier || 0));
  }, [selectedOutcome, numericAmount]);

  const maxBetAmount = useMemo(
    () => getMaxCasinoBetForOutcome(game, userCoinBalance, selectedOutcome),
    [game, userCoinBalance, selectedOutcome]
  );

  const suggestedBetAmounts = useMemo(
    () => getSuggestedCasinoBetAmounts(maxBetAmount),
    [maxBetAmount]
  );

  const canPlaceBet = maxBetAmount >= MIN_CASINO_BET;
  const maxFromPool = useMemo(() => {
    if (!selectedOutcome) return 0;
    const multiplier = Number(selectedOutcome.multiplier) || 1;
    const pool = Math.floor(Number(game?.allocatedAmount) || 0);
    return Math.floor(pool / multiplier);
  }, [game?.allocatedAmount, selectedOutcome]);

  const resetForm = useCallback(() => {
    setStep(1);
    setSelectedOutcome(null);
    setExactNumber("");
    setBetAmount("");
    setError("");
  }, []);

  useEffect(() => {
    if (open) return;
    resetForm();
  }, [open, resetForm]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [onClose, submitting]);

  useEffect(() => {
    if (!open) return;
    bodyRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [open, step]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) handleClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, handleClose, submitting]);

  const validateStep1 = () => {
    if (!selectedOutcome) {
      setError("Pick an outcome to bet on.");
      return false;
    }
    setError("");
    return true;
  };

  const validateStep2 = () => {
    if (isExactFlow) {
      if (!Number.isFinite(numericExact) || numericExact < 1 || numericExact > 36) {
        setError("Enter a number between 1 and 36.");
        return false;
      }
    }
    if (!numericAmount || numericAmount < MIN_CASINO_BET) {
      setError(`Minimum bet is ${formatCasinoCoin(MIN_CASINO_BET)} COIN.`);
      return false;
    }
    if (numericAmount > userCoinBalance) {
      setError("You do not have enough COIN for this bet.");
      return false;
    }
    if (numericAmount > maxBetAmount) {
      setError(`Maximum bet for ×${selectedOutcome?.multiplier} is ${formatCasinoCoin(maxBetAmount)} COIN.`);
      return false;
    }
    if (!canPlaceBet) {
      setError("This table cannot cover a minimum bet on the selected outcome.");
      return false;
    }
    setError("");
    return true;
  };

  const handleNext = () => {
    if (submitting) return;
    if (!validateStep1()) return;
    setBetAmount("");
    setError(
      canPlaceBet
        ? ""
        : maxBetAmount <= 0
          ? userCoinBalance < MIN_CASINO_BET
            ? `You need at least ${formatCasinoCoin(MIN_CASINO_BET)} COIN in your wallet.`
            : `This table cannot cover a ${formatCasinoCoin(MIN_CASINO_BET)} COIN bet on ×${selectedOutcome?.multiplier}. Try a lower multiplier (e.g. Red/Black ×2).`
          : `Max bet here is ${formatCasinoCoin(maxBetAmount)} COIN — below the ${formatCasinoCoin(MIN_CASINO_BET)} minimum. Pick a lower multiplier outcome.`
    );
    setStep(2);
  };

  const handleBack = () => {
    if (submitting) return;
    setStep(1);
    setError("");
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!validateStep2()) return;

    const payloadOutcome = { ...selectedOutcome };
    if (isExactFlow) {
      payloadOutcome.value = numericExact;
    }

    try {
      await onSubmit({
        selectedOutcome: payloadOutcome,
        betAmount: numericAmount,
      });
      handleClose();
    } catch (e) {
      setError(getCasinoActionErrorMessage(e, "Could not reverse ticket. Please try again."));
    }
  };

  if (!open || !game) return null;

  return createPortal(
    <div className="bsb-root" role="dialog" aria-modal="true" aria-labelledby="bsb-title">
      <button
        type="button"
        className="bsb-backdrop"
        aria-label="Close"
        onClick={handleClose}
        disabled={submitting}
      />

      <div className="bsb-sheet" onClick={(e) => e.stopPropagation()}>
        <header className="bsb-head">
          <div className="bsb-head-main">
            <span className="bsb-head-icon">
              {step === 1 ? <CircleDot size={17} /> : <Target size={17} />}
            </span>
            <div>
              <p className="bsb-head-kicker">Step {step} of 2 · Bond Spin</p>
              <h2 id="bsb-title">{step === 1 ? "Pick outcome" : "Enter stake"}</h2>
            </div>
          </div>
          <div className="bsb-head-balance">
            <Banknote size={12} />
            <strong>{formatCasinoCoin(userCoinBalance)} COIN</strong>
          </div>
          <button type="button" className="bsb-close" onClick={handleClose} disabled={submitting} aria-label="Close">
            <X size={16} />
          </button>
        </header>

        <div className="bsb-body" ref={bodyRef}>
          {error ? (
            <div className="bsb-error" role="alert">
              {error}
            </div>
          ) : null}

          {step === 1 ? (
            <>
              <p className="bsb-hint">Choose what you are betting on. Each outcome pays its own multiplier.</p>
              <div className="bsb-outcome-grid">
                {outcomes.map((outcome) => {
                  const active = selectedOutcome?.outcomeType === outcome.outcomeType;
                  const dot = OUTCOME_COLORS[outcome.outcomeType] || "#64B5F6";
                  const outcomeMaxBet = getMaxCasinoBetForOutcome(game, userCoinBalance, outcome);
                  const outcomeViable = canAffordCasinoMinBet(game, userCoinBalance, outcome);
                  return (
                    <button
                      key={outcome.outcomeType}
                      type="button"
                      className={`bsb-outcome-card ${active ? "selected" : ""}${outcomeViable ? "" : " bsb-outcome-card-tight"}`}
                      onClick={() => {
                        setSelectedOutcome(outcome);
                        setBetAmount("");
                        setError("");
                      }}
                    >
                      <span className="bsb-outcome-dot" style={{ backgroundColor: dot }} />
                      <span className="bsb-outcome-copy">
                        <strong>{formatCasinoOutcomeLabel(outcome)}</strong>
                        <span>×{outcome.multiplier}</span>
                        <em className={outcomeViable ? "bsb-outcome-max ok" : "bsb-outcome-max warn"}>
                          Max {formatCasinoCoin(outcomeMaxBet)} COIN
                        </em>
                      </span>
                      {active ? <Check size={14} className="bsb-outcome-check" /> : null}
                    </button>
                  );
                })}
              </div>
              {selectedOutcome ? (
                <div className={`bsb-max-preview${canPlaceBet ? "" : " bsb-max-preview-warn"}`}>
                  <span>{canPlaceBet ? "Suggested max bet" : "Cannot meet min bet"}</span>
                  <strong>
                    {canPlaceBet
                      ? `${formatCasinoCoin(maxBetAmount)} COIN`
                      : `Need ${formatCasinoCoin(MIN_CASINO_BET)} · max ${formatCasinoCoin(maxBetAmount)}`}
                  </strong>
                  <em>
                    {canPlaceBet
                      ? `Pool ÷ ×${selectedOutcome.multiplier}`
                      : "Try Red/Black ×2 or another table"}
                  </em>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="bsb-selected-pill">
                <span>Your pick</span>
                <strong>{formatCasinoOutcomeLabel(selectedOutcome)}</strong>
                <em>×{selectedOutcome?.multiplier}</em>
              </div>

              {isExactFlow ? (
                <section className="bsb-panel">
                  <h3>Exact number</h3>
                  <p className="bsb-panel-hint">Enter a number from 1 to 36.</p>
                  <div className="bsb-number-field">
                    <span>#</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={exactNumber}
                      onChange={(e) => {
                        setExactNumber(e.target.value.replace(/[^0-9]/g, "").slice(0, 2));
                        setError("");
                      }}
                      placeholder="17"
                      aria-label="Exact number"
                    />
                  </div>
                </section>
              ) : null}

              <section className="bsb-panel">
                <h3>Bet amount</h3>
                <p className="bsb-panel-hint">
                  Min {formatCasinoCoin(MIN_CASINO_BET)} COIN · max{" "}
                  {formatCasinoCoin(maxBetAmount)} COIN at ×{selectedOutcome?.multiplier}.
                </p>
                <div className="bsb-limit-row">
                  <div className="bsb-limit-card">
                    <span>Your wallet</span>
                    <strong>{formatCasinoCoin(userCoinBalance)}</strong>
                  </div>
                  <div className="bsb-limit-card">
                    <span>Table limit</span>
                    <strong>{formatCasinoCoin(maxFromPool)}</strong>
                  </div>
                  <div className="bsb-limit-card bsb-limit-card-accent">
                    <span>Max bet</span>
                    <strong>{formatCasinoCoin(maxBetAmount)}</strong>
                  </div>
                </div>
                <div className="bsb-cash-field">
                  <span>COIN</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={betAmount}
                    onChange={(e) => {
                      const next = e.target.value.replace(/[^0-9]/g, "");
                      setBetAmount(next);
                      setError("");
                    }}
                    placeholder={String(Math.min(MIN_CASINO_BET, maxBetAmount) || MIN_CASINO_BET)}
                    aria-label="Bet amount"
                  />
                  <button
                    type="button"
                    className="bsb-max-btn"
                    disabled={!canPlaceBet || submitting}
                    onClick={() => {
                      setBetAmount(String(maxBetAmount));
                      setError("");
                    }}
                  >
                    Max
                  </button>
                </div>
                <div className="bsb-chip-grid">
                  {suggestedBetAmounts.map((amount) => {
                    const active = numericAmount === amount;
                    const isMaxChip = amount === maxBetAmount;
                    return (
                      <button
                        key={amount}
                        type="button"
                        className={`bsb-chip ${active ? "active" : ""} ${isMaxChip ? "max" : ""}`}
                        onClick={() => {
                          setBetAmount(String(amount));
                          setError("");
                        }}
                      >
                        {isMaxChip ? `Max · ${formatCasinoCoin(amount)}` : formatCasinoCoin(amount)}
                      </button>
                    );
                  })}
                </div>
              </section>

              {numericAmount >= MIN_CASINO_BET ? (
                <div className="bsb-win-preview">
                  <span>Potential win</span>
                  <strong>{formatCasinoCoin(potentialWin)} COIN</strong>
                </div>
              ) : null}
            </>
          )}
        </div>

        <footer className="bsb-foot">
          <div className="bsb-summary">
            {step === 1 ? (
              <>
                <span>Selected</span>
                <strong>{selectedOutcome ? formatCasinoOutcomeLabel(selectedOutcome) : "None yet"}</strong>
              </>
            ) : (
              <>
                <span>Max bet</span>
                <strong>{formatCasinoCoin(maxBetAmount)} COIN</strong>
              </>
            )}
          </div>
          <div className="bsb-foot-actions">
            {step > 1 ? (
              <button
                type="button"
                className="bsb-btn bsb-btn-ghost bsb-btn-icon"
                onClick={handleBack}
                disabled={submitting}
                aria-label="Back"
              >
                <ChevronLeft size={18} />
              </button>
            ) : (
              <button type="button" className="bsb-btn bsb-btn-ghost" onClick={handleClose} disabled={submitting}>
                Cancel
              </button>
            )}
            <button
              type="button"
              className="bsb-btn bsb-btn-primary"
              onClick={step === 1 ? handleNext : handleSubmit}
              disabled={
                submitting ||
                (step === 1 && !selectedOutcome) ||
                (step === 2 &&
                  (!canPlaceBet ||
                    numericAmount < MIN_CASINO_BET ||
                    numericAmount > maxBetAmount ||
                    numericAmount > userCoinBalance ||
                    (isExactFlow && (!Number.isFinite(numericExact) || numericExact < 1 || numericExact > 36))))
              }
            >
              {submitting ? (
                <>
                  <Loader2 size={15} className="bsb-spin" />
                  Reversing…
                </>
              ) : step === 1 ? (
                <>
                  Continue
                  <ChevronRight size={16} />
                </>
              ) : (
                <>Reverse ticket · {formatCasinoCoin(numericAmount || MIN_CASINO_BET)} COIN</>
              )}
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default PlaceBondSpinBetModal;
